/**
 * Composer · AI 智能分类对话输入框（v4）
 *
 * v4 改造点
 * ───────────────────────────────────────────────────────────
 * · 不再 dispatch user TextMessage，改为通过 useAIClassifySession() 暴露的
 *   sendUserMessage(text) 入口，由 hook 内部统一编排消息追加 + SSE 调用
 * · 底部按钮区改为消费 AssistantMessage.payload.bottomOptions[] 渲染
 * · 首次进入（无消息）保留 QuickPrompts 兜底
 * · session.status === 'streaming' 时整体禁用
 *
 * sendUserMessage 通过 React Context 由父级（AIClassifyPage）注入；
 * 这样 hook 实例只有一份，不需要在每个组件里各跑一份监听副作用。
 */

import React, { useCallback, useRef, useState } from 'react'
import classNames from 'classnames'
import { Button, Input } from 'antd'
import { ArrowUpOutlined } from '@ant-design/icons'
import { useAppSelector } from '../../../../../../app/hooks'
import {
  selectChatIsEmpty,
  selectLatestBottomOptions,
  selectSessionStatus
} from '../../../../../../slices/aiClassify'
import BottomOptions from './BottomOptions'
import FolderPathBar from './FolderPathBar'
import SurveyEntryBanner from '../../../ImageRecognitionSurvey'
import { useChatActions } from '../../ChatActionsContext'
import { trackAIChatClick } from '../../../../utils/trackClassify'
import styles from './Composer.module.scss'

const PLACEHOLDER_NORMAL = '描述你想如何整理照片...'
const PLACEHOLDER_STREAMING = 'AI 正在响应，请稍候...'
const PLACEHOLDER_ERROR = '上一轮出错了，可以重新输入或修改提问'

function getRawTextarea(
  textAreaRef: React.RefObject<any>
): HTMLTextAreaElement | null {
  const inst: any = textAreaRef.current
  return inst?.resizableTextArea?.textArea ?? null
}

const Composer: React.FC = () => {
  const { sendUserMessage } = useChatActions()
  const sessionStatus = useAppSelector(selectSessionStatus)
  const bottomOptions = useAppSelector(selectLatestBottomOptions)
  // 缺省态（无任何消息）· 问卷入口仅此时显示，进入会话（SSE/历史）后隐藏
  const isChatEmpty = useAppSelector(selectChatIsEmpty)

  const [value, setValue] = useState('')
  const textAreaRef = useRef<any>(null)

  // 流式期间禁用
  const isDisabled = sessionStatus === 'streaming'
  const isEmptyValue = value.trim().length === 0

  // 底部按钮区现在只剩服务端 bottomOptions（次低优先级）
  //
  // 注（v6 调整）：
  //   · QuickPrompts（首次推荐）已移到 ChatFeed 的欢迎气泡下方 · 横排自动换行
  //   · 确认创建入口在 FolderPlanPreview 卡内（useConfirmCreate），不再占用 Composer 底部
  //   两组操作按钮都不再占用 Composer 底部，输入框视觉焦点不被遮挡
  const showBottomOptions = bottomOptions.length > 0 && !isDisabled

  const doSend = useCallback(
    (text: string) => {
      const content = text.trim()
      if (!content) return
      if (isDisabled) return
      trackAIChatClick('send', { string1: content })
      sendUserMessage(content)
      setValue('')
      const el = getRawTextarea(textAreaRef)
      if (el) el.focus()
    },
    [isDisabled, sendUserMessage]
  )

  const handleSendClick = useCallback(() => {
    doSend(value)
  }, [doSend, value])

  const handleBottomOptionSelect = useCallback(
    (optionValue: string) => {
      doSend(optionValue)
    },
    [doSend]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter') return
      if (e.shiftKey) return
      // @ts-expect-error · isComposing 在部分 TS lib 里未声明
      if (e.isComposing || e.keyCode === 229) return
      e.preventDefault()
      doSend(value)
    },
    [doSend, value]
  )

  const placeholder =
    sessionStatus === 'streaming'
      ? PLACEHOLDER_STREAMING
      : sessionStatus === 'error'
        ? PLACEHOLDER_ERROR
        : PLACEHOLDER_NORMAL

  return (
    <div
      className={classNames(styles.composer, {
        [styles.disabled]: isDisabled
      })}
    >
      {showBottomOptions && (
        <BottomOptions
          options={bottomOptions}
          onSelect={handleBottomOptionSelect}
          disabled={isDisabled}
        />
      )}

      {/* AI 图像识别问卷入口 · 紧贴输入框上方（Figma 12:3097）· 仅缺省态显示，
          发送消息（SSE 会话）或切到历史会话后隐藏，避免挤占会话空间 */}
      {isChatEmpty && <SurveyEntryBanner />}

      <div className={styles.inputWrap}>
        {/* 当前文件夹上下文 · 仅文件夹详情页有值时显示在输入框内顶部，可点 X 移除 */}
        <FolderPathBar />
        <div className={styles.inputRow}>
          <Input.TextArea
            ref={textAreaRef}
            className={styles.textarea}
            value={value}
            disabled={isDisabled}
            placeholder={placeholder}
            // minRows 2 · 默认即给两行高度，输入框看着更"敦实"，长一点的描述也不挤
            // maxRows 6 · 超过自动出滚动条，避免一句话把抽屉撑爆
            autoSize={{ minRows: 2, maxRows: 6 }}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            type="primary"
            shape="circle"
            size="small"
            className={styles.sendButton}
            disabled={isDisabled || isEmptyValue}
            onClick={handleSendClick}
            aria-label="发送"
          >
            <ArrowUpOutlined />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Composer
